/**
 * The install invitation's memory and rules (docs/mockups/install.html, Spec panel 1).
 * One versioned blob, `arrow:v1:install`, parsed defensively; the rules are pure
 * functions of it, the date and what's on screen, so they're tested without React.
 *
 * Whether Sparrow is installed is never stored: it's detected each time (pwa/install.ts:
 * running standalone, `appinstalled` this page load, `getInstalledRelatedApps()`, and a
 * `beforeinstallprompt` meaning "not installed") and passed in as `installed`. A stored
 * flag went stale on uninstall and hid every way back in.
 */
import { addDays, type DateKey, isDateKey } from '@arrow/game-engine';

import type { KeyValueStore } from '../storage/kv.ts';

export const INSTALL_KEY = 'arrow:v1:install';

/** Where an invitation was shown (the `surface` prop of the install events). */
export type Surface = 'today_card' | 'menu' | 'stats_row' | 'desktop_button' | 'notifications';
/** `notifications`: an ask for notifications on an iPhone in Safari, which leads to the guide. */
export const SURFACES: readonly Surface[] = ['today_card', 'menu', 'stats_row', 'desktop_button', 'notifications'];

export interface InstallState {
	v: 1;
	/**
	 * Analytics only, never a rule: the first day `appinstalled` fired or Sparrow opened
	 * from the home screen on this storage (so `installed { via: 'first_standalone_open' }`
	 * fires once, and `opened_standalone` can say `daysSinceInstall`). Nothing reads it to
	 * hide anything.
	 */
	installedOn: DateKey | null;
	/** How many times the invitation was turned down ("Not now", closing the guide, Chrome's Cancel). */
	dismissals: number;
	/** The card rests until this day (exclusive): it may show again on `restUntil`. */
	restUntil: DateKey | null;
	/** Turned down three times: never again (the menu line stays). */
	never: boolean;
	/** The last day each surface was shown, and how many times in all (for `timesShown`). */
	shown: Partial<Record<Surface, { last: DateKey; times: number }>>;
	/** The day the Today card's daily decision was made, and what it was. */
	cardDay: { day: DateKey; show: boolean } | null;
}

export const EMPTY_STATE: InstallState = { v: 1, installedOn: null, dismissals: 0, restUntil: null, never: false, shown: {}, cardDay: null };

/** "Not now" rests the card for 7 days, the second time 30, the third time for good. */
export const REST_DAYS = [7, 30] as const;
/** Finishing the iPhone guide ("Done") rests it for 30 days: iOS can't tell us it worked. */
export const GUIDE_DONE_REST_DAYS = 30;


const isSurface = (v: unknown): v is Surface => typeof v === 'string' && (SURFACES as readonly string[]).includes(v);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** Parses a stored blob. Anything unreadable reads as a fresh state; never throws. */
export function parseInstallState(raw: string | null): InstallState {
	if (raw === null) return { ...EMPTY_STATE, shown: {} };
	try {
		const o = JSON.parse(raw) as Record<string, unknown>;
		if (typeof o !== 'object' || o === null || o.v !== 1) return { ...EMPTY_STATE, shown: {} };
		// Older blobs kept an `installed: { on, via }` mark that hid the install surfaces; only its day survives, as analytics.
		const old = o.installed as Record<string, unknown> | null | undefined;
		const installedOn = isDateKey(o.installedOn) ? o.installedOn : old && isDateKey(old.on) ? old.on : null;
		const shown: InstallState['shown'] = {};
		if (typeof o.shown === 'object' && o.shown !== null) {
			for (const [k, val] of Object.entries(o.shown as Record<string, unknown>)) {
				const s = val as Record<string, unknown> | null;
				if (isSurface(k) && s && isDateKey(s.last) && isCount(s.times)) shown[k] = { last: s.last, times: s.times };
			}
		}
		const cd = o.cardDay as Record<string, unknown> | null | undefined;
		return {
			v: 1,
			installedOn,
			dismissals: isCount(o.dismissals) ? Math.min(o.dismissals, 99) : 0,
			restUntil: isDateKey(o.restUntil) ? o.restUntil : null,
			never: o.never === true,
			shown,
			cardDay: cd && isDateKey(cd.day) && typeof cd.show === 'boolean' ? { day: cd.day, show: cd.show } : null,
		};
	} catch {
		return { ...EMPTY_STATE, shown: {} };
	}
}

export function loadInstallState(store: KeyValueStore): InstallState {
	return parseInstallState(store.get(INSTALL_KEY));
}

export function saveInstallState(store: KeyValueStore, state: InstallState): void {
	store.set(INSTALL_KEY, JSON.stringify(state));
}

/** A stored blob still carrying the old `installed` mark: rewrite it without (returns whether it did). */
export function migrateInstallState(store: KeyValueStore): boolean {
	const raw = store.get(INSTALL_KEY);
	if (raw === null || !raw.includes('"installed"')) return false;
	saveInstallState(store, parseInstallState(raw));
	return true;
}

// ------------------------------------------------------------------------------- the rest

/** "Not now" (or closing the guide, or Chrome's Cancel): 7 days, then 30, then for good. */
export function afterNotNow(state: InstallState, today: DateKey): InstallState {
	const dismissals = state.dismissals + 1;
	const days = REST_DAYS[dismissals - 1];
	return { ...state, dismissals, never: days === undefined, restUntil: days === undefined ? state.restUntil : addDays(today, days) };
}

/** "Done" in the iPhone guide: rest 30 days (it may well have worked; iOS can't say). */
export function afterGuideDone(state: InstallState, today: DateKey): InstallState {
	const until = addDays(today, GUIDE_DONE_REST_DAYS);
	return { ...state, restUntil: state.restUntil && state.restUntil > until ? state.restUntil : until };
}

/** Remember the install day for analytics (first time only). */
export function noteInstalledOn(state: InstallState, today: DateKey): InstallState {
	return state.installedOn ? state : { ...state, installedOn: today };
}

export function isResting(state: InstallState, today: DateKey): boolean {
	return state.never || (state.restUntil !== null && today < state.restUntil);
}

/** Notes that `surface` was shown today. Returns the new state and whether this was its first time today. */
export function noteShown(state: InstallState, surface: Surface, today: DateKey): { state: InstallState; first: boolean; times: number } {
	const prev = state.shown[surface];
	if (prev?.last === today) return { state, first: false, times: prev.times };
	const times = (prev?.times ?? 0) + 1;
	return { state: { ...state, shown: { ...state.shown, [surface]: { last: today, times } } }, first: true, times };
}

// ------------------------------------------------------------------------------ the rules

export interface CardContext {
	/** Sparrow is installed, as detected now (running standalone, `appinstalled` this load, getInstalledRelatedApps). */
	installed: boolean;
	/** Dailies completed on this device, ever (any level). */
	dailiesCompleted: number;
	/** The current route is Today (`/`). */
	onToday: boolean;
	/** The platform has a way to install from here (`installRoute` isn't null, and isn't the Mac's menu-only route). */
	hasRoute: boolean;
	/** Another notice is on Today (offline, the challenge line…). */
	otherNotice: boolean;
	today: DateKey;
}

/** Spec panel 1, rules 1–5: may the Today card appear at all, ignoring "once a day"? */
export function cardEligible(state: InstallState, ctx: Omit<CardContext, 'otherNotice'>): boolean {
	if (ctx.installed) return false;
	if (ctx.dailiesCompleted < 1) return false;
	if (!ctx.onToday || !ctx.hasRoute) return false;
	return !isResting(state, ctx.today);
}

/**
 * The Today card's daily decision (rule 6): the first time the card is eligible on a day,
 * it shows unless another notice is up, in which case it waits for tomorrow. Once shown,
 * that day is spent: it isn't shown again on later visits to Today the same day (the
 * screen that showed it keeps it until it's left). Returns the decision and the state to
 * save (the day's decision is remembered).
 */
export function decideCard(state: InstallState, ctx: CardContext): { show: boolean; state: InstallState } {
	if (!cardEligible(state, ctx)) return { show: false, state };
	if (state.cardDay?.day === ctx.today) return { show: false, state };
	const show = !ctx.otherNotice;
	return { show, state: { ...state, cardDay: { day: ctx.today, show } } };
}

/**
 * The always-there line (C): the ≡ menu line and the row at the foot of Stats. Shown
 * whenever Sparrow isn't detected as installed and there's a way to install from here,
 * first visit or not, rests or not. Where nothing can tell (an iPhone Safari tab,
 * Firefox), that means shown. The menu also leaves it out inside an app's own browser
 * (the way-out card does that job) and in a game.
 */
export function lineVisible(ctx: { installed: boolean; hasRoute: boolean }): boolean {
	return !ctx.installed && ctx.hasRoute;
}

/** The desktop button beside the streak: Chrome / Edge holding a prompt, the card's rest rules. */
export function desktopButtonVisible(state: InstallState, ctx: { installed: boolean; held: boolean; desktop: boolean; dailiesCompleted: number; today: DateKey }): boolean {
	return ctx.desktop && ctx.held && !ctx.installed && ctx.dailiesCompleted >= 1 && !isResting(state, ctx.today);
}

/** Whole calendar days since the install day (0 when unknown), for `opened_standalone`. */
export function daysSinceInstall(state: InstallState, today: DateKey, daysBetween: (a: DateKey, b: DateKey) => number): number {
	if (!state.installedOn) return 0;
	try {
		return Math.max(0, daysBetween(state.installedOn, today));
	} catch {
		return 0;
	}
}
